import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { DialogClose } from '@/components/DialogClose';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

type Props = {
  open: boolean;
  title: string;
  description?: ReactNode;
  /** "Confirm" button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** "Cancel" button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Use error tone for destructive actions. */
  destructive?: boolean;
  /** Disabled while a mutation is in flight. */
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Standard confirm/cancel dialog. Used for delete, lock, and similar
 * destructive flows. The body slot accepts arbitrary ReactNode so callers
 * can render structured detail (lists of affected rows, etc.).
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {title}
        <DialogClose onClose={onCancel} />
      </DialogTitle>
      {description && (
        <DialogContent sx={{ pt: '20px !important', pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          {typeof description === 'string'
            ? <Typography variant="body2" color="text.secondary">{description}</Typography>
            : description}
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>{cancelLabel}</Button>
        <Button
          onClick={onConfirm}
          disabled={busy}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          startIcon={destructive && !busy ? <DeleteOutlineIcon /> : undefined}
        >
          {busy ? 'Working…' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
